<script setup lang="ts">
/**
 * Лендинг Keypi.
 *
 * Единственный экран продукта со своим визуальным языком: кремовая бумага,
 * Piazzolla в заголовках, моноширинные пометки на полях. Это намеренно — весь
 * лендинг построен на приёме «страница, которую вы читаете, собрана в Keypi»,
 * и выглядеть она должна как документ, а не как интерфейс. Приложение за
 * логином остаётся прежним: там человек работает, а не читает.
 *
 * Из-за этого стили здесь не утилитами Tailwind, а своим набором классов с
 * префиксом lp-. Палитра лендинга не пересекается с продуктовой ни одним
 * цветом, и выражать её через bg-[#fffdf7] в каждом теге значило бы получить
 * разметку, в которой не видно содержимого. Файл со стилями лежит рядом и
 * уезжает в отдельный чанк вместе со страницей: три шрифтовых семейства
 * нужны только здесь, и платить за них внутри приложения незачем.
 *
 * Цены не вшиты. Линейку и цену документа страница берёт с сервера: их правит
 * суперадмин в админке, и вшитая копия однажды показала бы не ту цену, по
 * которой выставляется счёт. Приезжают они уже в разметке — их подставляет
 * пререндер (`prerender.mjs`), а не запрос из браузера; общий источник для
 * всех публичных страниц лежит в `src/site/public-data.ts`.
 *
 * Кнопки «Войти» и «Начать бесплатно» — обычные ссылки, а не RouterLink. Вход
 * и регистрация живут в кабинете, то есть в другой точке входа и другом
 * бандле; маршрута с таким именем в роутере витрины нет, и RouterLink молча
 * увёл бы на несуществующий адрес. Переход к соседним правовым документам,
 * наоборот, остаётся маршрутом: они в том же бандле.
 */
import { formatPrice, pluralize } from '@zetter/shared';
import { computed } from 'vue';
import { RouterLink } from 'vue-router';
import { DOC_KINDS, TEMPLATE_TOTAL } from '../../site/catalog.js';
import { faqItems } from '../../site/faq.js';
import { legal, pricing } from '../../site/public-data.js';
import TaskField from './TaskField.vue';
import './landing.css';

/**
 * Частые вопросы и состав каталога приходят из общих модулей, а не набраны
 * здесь. Тот же текст читает `seo.ts`: вопросы уезжают в разметку `FAQPage`,
 * состав каталога — в llms.txt и в описание продукта для schema.org. Копия в
 * шаблоне разошлась бы с ними на первой же правке формулировки, и разошлась бы
 * молча — разметку никто не перечитывает.
 */
const faq = computed(() => faqItems({ pricing: pricing.value, legal: legal.value }));

/** Сколько готовых документов дарится новой организации. */
const welcomeDocs = computed(() => pricing.value?.welcomeCredits ?? 1);

/**
 * Цена готового документа: сборка и правки к ней.
 *
 * Единица счёта — документ, а не правка: правки внутри одного документа
 * отличаются по цене в сотню раз, от подписи под картинкой до пересборки
 * структуры. Обещать «N правок» значит обещать величину, которой не
 * существует, — поэтому и план, и остаток считаются документами.
 */
const docPrice = computed(() =>
  pricing.value ? formatPrice(pricing.value.creditKopecks) : '—',
);

/** Линейка по возрастанию цены. Пробный идёт первым — он и есть вход. */
const plans = computed(() => pricing.value?.plans ?? []);

/**
 * План, который предлагается по умолчанию.
 *
 * Не самый дорогой и не самый дешёвый: первый, чей месячный пакет закрывает
 * хотя бы один готовый документ. На странице цен должно быть видно, с чего
 * начинать, а «с чего начинать» — это «сколько документов в месяц вы делаете».
 */
const recommendedKey = computed(
  () => plans.value.find((plan) => plan.includedCredits >= 5)?.key ?? null,
);

/**
 * Сколько документов в месяц закрывает включённый пакет.
 *
 * Число точное, а не «около»: кредит списывается за документ, а не за токены,
 * поэтому баланс движется целыми кредитами. Раньше здесь стояло «около» —
 * честная оговорка при списании по токенам, когда цена документа расходилась
 * в семнадцать раз и обещание неизбежно расходилось с фактом.
 */
function docsPerMonth(credits: number): string {
  if (credits === 0) return 'без включённых документов';
  return `${credits} ${pluralize(credits, ['документ', 'документа', 'документов'])} в месяц`;
}

function limit(value: number | null): string {
  return value === null ? 'без границ' : value.toLocaleString('ru-RU');
}
</script>

<template>
  <div class="lp">
    <!-- ─── шапка ─────────────────────────────────────────────────────── -->
    <header class="lp-topbar">
      <div class="lp-wrap lp-topbar__inner">
        <a href="#top" class="lp-brand">
          <span class="lp-brand__mark">K</span>
          <span class="lp-brand__name">Keypi</span>
          <span class="lp-brand__tag">task → proposal</span>
        </a>
        <div class="lp-spacer" />
        <a href="#types" class="lp-navlink">Что собирает</a>
        <!-- Каталог — отдельная страница, а не якорь: с него начинают те, кто
             пришёл из поиска за конкретным шаблоном. -->
        <a href="/templates" class="lp-navlink">Шаблоны</a>
        <a href="#how" class="lp-navlink">Как это работает</a>
        <a href="#faq" class="lp-navlink">Вопросы</a>
        <a href="#pricing" class="lp-navlink">Цены</a>
        <a href="/login" class="lp-navlink">Войти</a>
        <a href="/register" class="lp-btn lp-btn--primary">Начать бесплатно</a>
      </div>
    </header>

    <!--
      Рамка «эта страница — документ, собранный в Keypi». Не украшение: она и
      есть главное доказательство продукта: сама страница собрана агентом.
    -->
    <div class="lp-meta">
      <div class="lp-wrap lp-meta__inner">
        <span>ЛЕНДИНГ № 2026-001 · ПОЛУЧАТЕЛЬ: АГЕНТСТВА И СТУДИИ · ИСПОЛНИТЕЛЬ: АГЕНТ KEYPI</span>
        <span>ВЕРСИЯ 12 · АКТУАЛЬНАЯ · СОБРАНО ЗА 17 МИНУТ</span>
      </div>
    </div>

    <!-- ─── первый экран ──────────────────────────────────────────────── -->
    <section id="top" class="lp-wrap lp-hero">
      <div>
        <div class="lp-eyebrow">НЕЙРОСЕТЬ ДЛЯ КП, ЛЕНДИНГОВ, ПРЕЗЕНТАЦИЙ И ПРАЙСОВ</div>
        <!--
          Перенос задан вручную. «Коммерческое предложение» в заголовочном
          кегле не помещается в колонку ни при каком разумном размере, и
          автоматическая балансировка ставила третью строку с тире в начале.
          Явный разрыв делит фразу там, где она делится по смыслу, а неразрывный
          пробел внутри кавычек не даёт балансировщику оторвать открывающую
          кавычку от слова и унести «пятнице»» на строку одну.
        -->
        <h1 class="lp-h1">Коммерческое предложение —<br />сегодня, а не «к пятнице»</h1>
        <p class="lp-lede">
          Keypi — ИИ-агент, который собирает коммерческое предложение, презентацию, лендинг или
          прайс из ваших материалов: брифа, таблицы с ценами, записи созвона. Через час у вас
          готовый документ в фирменном стиле — по ссылке, которую не стыдно отправить.
        </p>

        <ul class="lp-bullets">
          <li>
            Приложите материалы — бриф, прайс, запись созвона: агент прочитает и расшифрует сам
          </li>
          <li>Скажите правки словами — в чате или голосом, без ТЗ дизайнеру</li>
          <li>Отправьте клиенту ссылку — или скачайте PDF и PPTX той же кнопкой</li>
          <li>Соберите документ прямо в карточке сделки amoCRM, не выходя из CRM</li>
        </ul>

        <TaskField kind="offer" />

        <p class="lp-note">
          {{ welcomeDocs }}
          {{ pluralize(welcomeDocs, ['документ', 'документа', 'документов']) }} бесплатно · без
          карты · первая версия — примерно через 15 минут
        </p>
      </div>

      <!-- Стопка документов: то, что клиент получит, показано, а не описано. -->
      <div class="lp-stage" aria-hidden="true">
        <div class="lp-paper lp-paper--back" />
        <div class="lp-paper lp-paper--front">
          <div class="lp-doc__head">
            <span class="lp-doc__dot" />
            <span class="lp-mono-xs">СЕВЕРНЫЙ ВЕТЕР · КП № 214</span>
          </div>
          <div class="lp-doc__title">Монтаж и обслуживание вентиляции</div>
          <div class="lp-doc__sub">Бизнес-центр «Северный ветер», 12 400 м²</div>
          <div class="lp-doc__line" style="width: 88%" />
          <div class="lp-doc__line" style="width: 72%" />
          <div class="lp-doc__line" style="width: 81%" />
          <div class="lp-doc__table">
            <div class="lp-doc__row">
              <span>Этап 1 · Проект и закупка</span><b>140 000 ₽</b>
            </div>
            <div class="lp-doc__row">
              <span>Этап 2 · Монтаж, 3–4 этажи</span><b>265 000 ₽</b>
            </div>
            <div class="lp-doc__row">
              <span>Пусконаладка и паспорт системы</span><b>75 000 ₽</b>
            </div>
            <div class="lp-doc__row lp-doc__row--total">
              <span>Итого до 1 ноября</span><b>480 000 ₽</b>
            </div>
          </div>
          <div class="lp-doc__tags">
            <span>гарантия 24 мес</span>
            <span>СРО · допуски</span>
            <span class="lp-doc__tag--warm">скидка до 20.08</span>
          </div>
        </div>

        <div class="lp-stamp">KEYPI · ВЕРСИЯ 12</div>

        <div class="lp-voice">
          <div class="lp-voice__label">ПРАВКА · ГОЛОСОМ · 0:07</div>
          <div class="lp-voice__text">
            «Разбей смету на два этапа и добавь пусконаладку отдельной строкой»
          </div>
        </div>

        <div class="lp-done">
          <svg viewBox="0 0 20 20" fill="currentColor">
            <path
              d="M16.7 5.3a1 1 0 0 1 0 1.4l-7.5 7.5a1 1 0 0 1-1.4 0l-3.5-3.5a1 1 0 1 1 1.4-1.4l2.8 2.79 6.8-6.79a1 1 0 0 1 1.4 0Z"
            />
          </svg>
          <span>Готово · версия 13 · документ собран</span>
        </div>
      </div>
    </section>

    <!-- ─── 01 · ага-момент ───────────────────────────────────────────── -->
    <section class="lp-wrap lp-section">
      <div class="lp-head">
        <span class="lp-num">01</span>
        <h2 class="lp-h2">Проверьте на своём КП или лендинге — это десять минут</h2>
      </div>
      <p class="lp-sub">
        Без демо по звонку и недели внедрения: загрузите живой материал — и посмотрите, что
        соберёт агент.
      </p>

      <div class="lp-grid lp-grid--2">
        <article class="lp-card">
          <div class="lp-mono-label">СПОСОБ 01</div>
          <h3 class="lp-h3">Загрузите запись созвона</h3>
          <p class="lp-p">
            Агент расшифрует её по говорящим, вытащит задачи, сроки и цифры — и разложит по
            разделам КП. Слушать час записи не придётся ни вам, ни ему.
          </p>
          <div class="lp-flow">
            <div class="lp-flow__box">
              <div class="lp-mono-xs">ЗАПИСЬ_СОЗВОНА.M4A · 47:12</div>
              <div class="lp-flow__text">
                Спикер 1: …успеть до первого ноября…<br />Спикер 2: …смету разбейте по этажам…
              </div>
            </div>
            <svg class="lp-arrow" viewBox="0 0 20 20" fill="currentColor">
              <path
                d="M3 10a.75.75 0 0 1 .75-.75h9.69L10.22 6.03a.75.75 0 1 1 1.06-1.06l4.5 4.5a.75.75 0 0 1 0 1.06l-4.5 4.5a.75.75 0 1 1-1.06-1.06l3.22-3.22H3.75A.75.75 0 0 1 3 10Z"
              />
            </svg>
            <div class="lp-flow__box lp-flow__box--strong">
              <div class="lp-mono-xs">СТРУКТУРА КП</div>
              <div class="lp-flow__text lp-flow__text--ink">
                1. Задача объекта<br />2. Смета по этажам<br />3. Сроки: до 1 ноября
              </div>
            </div>
          </div>
        </article>

        <article class="lp-card">
          <div class="lp-mono-label">СПОСОБ 02</div>
          <h3 class="lp-h3">Скажите шаблону одну фразу</h3>
          <p class="lp-p">
            «Сделай в цветах клиента — тёмно-зелёный и золото». Агент перекрасит документ, поправит
            логотип и покажет, что именно поменял и почему.
          </p>
          <div class="lp-swatches">
            <div class="lp-swatch__row">
              <span class="lp-mono-xs lp-swatch__key">ДО</span>
              <span class="lp-swatch" style="background: #4f46e5" />
              <span class="lp-swatch" style="background: #9ca3af" />
              <span class="lp-swatch" style="background: #e5e7eb" />
              <span class="lp-swatch__note">шаблон каталога</span>
            </div>
            <div class="lp-swatch__row">
              <span class="lp-mono-xs lp-swatch__key">ПОСЛЕ</span>
              <span class="lp-swatch" style="background: #1e4d3b" />
              <span class="lp-swatch" style="background: #b48b3d" />
              <span class="lp-swatch" style="background: #f2ede1" />
              <span class="lp-swatch__note lp-swatch__note--green">
                фирменные цвета клиента · готово за 4 минуты
              </span>
            </div>
          </div>
        </article>
      </div>
    </section>

    <!-- ─── 02 · что вы получите ──────────────────────────────────────── -->
    <section class="lp-wrap lp-section">
      <div class="lp-head">
        <span class="lp-num">02</span>
        <h2 class="lp-h2">Что вы получите</h2>
      </div>
      <div class="lp-grid lp-grid--4 lp-grid--rule">
        <div>
          <h3 class="lp-h4">Документ за час</h3>
          <p class="lp-p lp-p--muted">
            Первая версия через 15 минут, ещё пара правок — и можно отправлять. Вместо двух дней
            ожидания.
          </p>
        </div>
        <div>
          <h3 class="lp-h4">Правки без дизайнера</h3>
          <p class="lp-p lp-p--muted">
            Словами в чате или голосом. Текст и блоки можно поправить руками прямо в превью — это
            бесплатно.
          </p>
        </div>
        <div>
          <h3 class="lp-h4">Ссылка всегда актуальна</h3>
          <p class="lp-p lp-p--muted">
            Поправили после отправки — клиент уже видит новое. Нужен файл — PDF и PPTX той же
            кнопкой.
          </p>
        </div>
        <div>
          <h3 class="lp-h4">Фирстиль закреплён</h3>
          <p class="lp-p lp-p--muted">
            Свои шаблоны организации: агент собирает по ним, и стиль не разъезжается от документа
            к документу.
          </p>
        </div>
      </div>
    </section>

    <!-- ─── 03 · что собирает агент ───────────────────────────────────── -->
    <!--
      Раздел появился поздно и не ради поиска. Лендинг рассказывал про КП, а
      самая большая группа каталога — лендинги: продукт умеет заметно больше,
      чем обещает страница, и человек уходил, не узнав, что пришёл по адресу.
      То, что заодно это открывает целую группу запросов, — следствие, а не
      причина.
    -->
    <section id="types" class="lp-wrap lp-section">
      <div class="lp-head">
        <span class="lp-num">03</span>
        <h2 class="lp-h2">Не только КП: лендинги, презентации, прайсы и меню</h2>
      </div>
      <p class="lp-sub">
        {{ TEMPLATE_TOTAL }} готовых шаблонов в каталоге — или чистый лист, если своё устройство
        документа уже в голове. Любой сохранённый проект становится шаблоном вашей организации.
      </p>

      <div class="lp-grid lp-grid--3">
        <article v-for="kind in DOC_KINDS" :key="kind.name" class="lp-card lp-card--tight">
          <div class="lp-type__count">
            {{ kind.count }} {{ pluralize(kind.count, ['ШАБЛОН', 'ШАБЛОНА', 'ШАБЛОНОВ']) }}
          </div>
          <h3 class="lp-h4">
            <a v-if="kind.href" :href="kind.href" class="lp-type__link">{{ kind.name }}</a>
            <template v-else>{{ kind.name }}</template>
          </h3>
          <p class="lp-p lp-p--muted">{{ kind.note }}</p>
          <p v-if="kind.examples" class="lp-type__list">{{ kind.examples }}</p>
        </article>
      </div>
    </section>

    <!-- ─── 04 · узнаёте себя ─────────────────────────────────────────── -->
    <section class="lp-dark">
      <div class="lp-wrap lp-dark__inner">
        <div class="lp-head">
          <span class="lp-num lp-num--light">04</span>
          <h2 class="lp-h2 lp-h2--light">Узнаёте себя?</h2>
        </div>
        <p class="lp-sub lp-sub--light">
          Вы хотите выигрывать больше проектов — и чтобы подготовка предложения перестала съедать
          вечера команды.
        </p>
        <div class="lp-grid lp-grid--2 lp-grid--quotes">
          <blockquote class="lp-quote">
            <p>«Клиент просит КП к завтрашнему утру, а дизайнер занят до четверга»</p>
            <cite>ЧЕТВЕРГ, 18:40</cite>
          </blockquote>
          <blockquote class="lp-quote">
            <p>«Поправил цифры сам — и вёрстка поехала: блоки съехали, шрифты разные, отправлять стыдно»</p>
            <cite>ПРАВКИ БЕЗ ДИЗАЙНЕРА</cite>
          </blockquote>
          <blockquote class="lp-quote">
            <p>«Созвон прошёл отлично — теперь два вечера собирать презентацию»</p>
            <cite>ПОСЛЕ ВСТРЕЧИ</cite>
          </blockquote>
          <blockquote class="lp-quote">
            <p>«Смета поменялась третий раз, и никто не помнит, какая версия у клиента»</p>
            <cite>КП_ФИНАЛ_2_НОВОЕ.PPTX</cite>
          </blockquote>
        </div>
      </div>
    </section>

    <!-- ─── 05 · как это работает ─────────────────────────────────────── -->
    <section id="how" class="lp-wrap lp-section">
      <div class="lp-head">
        <span class="lp-num">05</span>
        <h2 class="lp-h2">Как собрать коммерческое предложение или лендинг за час</h2>
      </div>
      <div class="lp-steps">
        <div class="lp-step">
          <span class="lp-step__num">ШАГ 01</span>
          <h3 class="lp-h3">Пришлите материалы</h3>
          <p class="lp-p">
            Бриф, прайс, макет, запись созвона — агент читает файлы и расшифровывает аудио сам.
            Ничего не нужно перепечатывать и пересказывать. Можно начать прямо из карточки
            сделки amoCRM: материалы и данные сделки уже там.
          </p>
        </div>
        <div class="lp-step">
          <span class="lp-step__num">ШАГ 02</span>
          <h3 class="lp-h3">Ответьте на вопросы по делу</h3>
          <p class="lp-p">
            Не анкета: агент спрашивает только то, чего не хватает, чтобы документ убеждал, — про
            клиента, цифры и что нужно доказать. Пропустили вопрос — решит сам и перечислит, что
            решил за вас.
          </p>
        </div>
        <div class="lp-step">
          <span class="lp-step__num">ШАГ 03</span>
          <h3 class="lp-h3">Правьте, как удобно</h3>
          <p class="lp-p">
            Суть — словами в чате, списание идёт по факту работы. Текст и блоки — руками прямо
            в превью, бесплатно и без ожидания. Каждая правка — версия, откат в один клик.
          </p>
        </div>
        <div class="lp-step">
          <span class="lp-step__num">ШАГ 04</span>
          <h3 class="lp-h3">Отправьте и следите</h3>
          <p class="lp-p">
            Ссылка показывает актуальную версию, вы видите, что клиент её открыл. Просят файл —
            PDF и PPTX собираются из того же документа.
          </p>
        </div>
      </div>
    </section>

    <!-- ─── 06 · к чему придёте ───────────────────────────────────────── -->
    <section class="lp-wrap lp-section">
      <div class="lp-head">
        <span class="lp-num">06</span>
        <h2 class="lp-h2">К чему вы придёте</h2>
      </div>
      <div class="lp-outcomes">
        <div class="lp-outcome">
          <span class="lp-outcome__num">ИТОГ 01</span>
          <span class="lp-outcome__text">
            Предложение уходит в день запроса — вы первыми ложитесь на стол клиенту.
          </span>
        </div>
        <div class="lp-outcome">
          <span class="lp-outcome__num">ИТОГ 02</span>
          <span class="lp-outcome__text">
            Дизайнеры делают проекты, за которые платят, — а не отступы в слайдах.
          </span>
        </div>
        <div class="lp-outcome">
          <span class="lp-outcome__num">ИТОГ 03</span>
          <span class="lp-outcome__text">
            Каждое следующее КП быстрее: библиотека ваших шаблонов растёт сама.
          </span>
        </div>
      </div>
    </section>

    <!-- ─── 07 · сомнения ─────────────────────────────────────────────── -->
    <section class="lp-wrap lp-section">
      <div class="lp-head">
        <span class="lp-num">07</span>
        <h2 class="lp-h2">У вас есть сомнения — правильно</h2>
      </div>
      <div class="lp-grid lp-grid--2">
        <article class="lp-card lp-card--tight">
          <h3 class="lp-h4">«У нас свой фирменный стиль»</h3>
          <p class="lp-p lp-p--muted">
            Сохраните готовый проект как шаблон организации — агент будет собирать следующие
            документы по нему. Шаблоны видны только вашей команде.
          </p>
        </article>
        <article class="lp-card lp-card--tight">
          <h3 class="lp-h4">«В документах — данные клиентов»</h3>
          <p class="lp-p lp-p--muted">
            Проекты видны только вашей организации. Ссылка отзывается в один клик, доступ
            уволившегося сотрудника закрывается мгновенно.
          </p>
        </article>
        <article class="lp-card lp-card--tight">
          <h3 class="lp-h4">«Непонятно, сколько это стоит»</h3>
          <p class="lp-p lp-p--muted">
            Считается в документах, а не в токенах: документ — {{ docPrice }}, и это вся его
            цена. Списывается на первой сборке, правки к готовому документу бесплатны. План
            включает пакет документов на месяц, перерасход докупается по той же цене. Каждое
            списание видно: кто, по какому проекту, сколько.
          </p>
        </article>
        <article class="lp-card lp-card--tight">
          <h3 class="lp-h4">«А если агент ошибётся?»</h3>
          <p class="lp-p lp-p--muted">
            Каждая правка — отдельная версия с откатом. Мелочь быстрее поправить руками прямо в
            превью — это бесплатно и не ждёт агента.
          </p>
        </article>
      </div>
    </section>

    <!-- ─── 08 · увольняем конкурентов ────────────────────────────────── -->
    <section class="lp-wrap lp-section">
      <div class="lp-head">
        <span class="lp-num">08</span>
        <h2 class="lp-h2">Что больше не понадобится</h2>
      </div>
      <div class="lp-grid lp-grid--3">
        <article class="lp-card lp-card--fired">
          <span class="lp-fired">БОЛЬШЕ НЕ НУЖЕН</span>
          <h3 class="lp-h3 lp-h3--narrow">Ночной PowerPoint</h3>
          <p class="lp-p lp-p--muted">
            Копипаст старых слайдов со следами прошлых клиентов и сломанной вёрсткой после каждой
            вставки.
          </p>
          <div class="lp-replace">
            <span class="lp-replace__arrow">→</span>
            <p>
              Агент собирает документ с нуля из ваших материалов — чужому названию взяться
              неоткуда.
            </p>
          </div>
        </article>
        <article class="lp-card lp-card--fired">
          <span class="lp-fired">БОЛЬШЕ НЕ НУЖЕН</span>
          <h3 class="lp-h3 lp-h3--narrow">Дизайнер на подряде</h3>
          <p class="lp-p lp-p--muted">
            Очередь, брифинг, «внесу правки в понедельник» — и счёт за каждый макет, как за
            проект.
          </p>
          <div class="lp-replace">
            <span class="lp-replace__arrow">→</span>
            <p>
              Час вместо двух суток, правки в тот же момент — и документ выходит дешевле одного
              макета на подряде.
            </p>
          </div>
        </article>
        <article class="lp-card lp-card--fired">
          <span class="lp-fired">БОЛЬШЕ НЕ НУЖЕН</span>
          <h3 class="lp-h3 lp-h3--narrow">Чат-бот + слайды руками</h3>
          <p class="lp-p lp-p--muted">
            Текст напишет, а верстать, красить и собирать PDF всё равно вам — и каждый раз заново.
          </p>
          <div class="lp-replace">
            <span class="lp-replace__arrow">→</span>
            <p>Keypi отдаёт свёрстанный документ: ссылка, PDF, PPTX — без ручной сборки.</p>
          </div>
        </article>
      </div>
    </section>

    <!-- ─── 09 · цены ─────────────────────────────────────────────────── -->
    <section id="pricing" class="lp-wrap lp-section">
      <div class="lp-head">
        <span class="lp-num">09</span>
        <h2 class="lp-h2">Цены: границы и объём работы в одной подписке</h2>
      </div>
      <p class="lp-sub">
        План — это месяц работы: границы команды плюс включённый пакет документов. Документ
        стоит {{ docPrice }}, и это вся его цена: списывается на первой сборке, а правки к
        готовому документу бесплатны — правьте, пока не устроит. Сделали больше, чем в
        пакете, — докупаете по той же цене, работа не встаёт. Неизрасходованное переносится на
        следующий месяц, но не больше одного пакета.
      </p>

      <div v-if="plans.length" class="lp-plans">
        <article
          v-for="plan in plans"
          :key="plan.key"
          class="lp-plan"
          :class="plan.key === recommendedKey ? 'lp-plan--pick' : ''"
        >
          <div class="lp-plan__top">
            <span class="lp-plan__title">{{ plan.title }}</span>
            <span v-if="plan.key === recommendedKey" class="lp-plan__badge">ВЫБОР БОЛЬШИНСТВА</span>
          </div>

          <div class="lp-plan__price">
            <template v-if="plan.monthlyPriceKopecks === 0">бесплатно</template>
            <template v-else>
              {{ formatPrice(plan.monthlyPriceKopecks) }}
              <span class="lp-plan__per">· в месяц</span>
            </template>
          </div>

          <div class="lp-plan__credits">{{ docsPerMonth(plan.includedCredits) }}</div>

          <dl class="lp-plan__rows">
            <div><dt>Участники</dt><dd>{{ limit(plan.members) }}</dd></div>
            <div><dt>Проекты</dt><dd>{{ limit(plan.projects) }}</dd></div>
            <div><dt>Правки и сборки параллельно</dt><dd>{{ plan.concurrentSessions }}</dd></div>
            <div>
              <dt>Хранение версий</dt>
              <dd>
                {{ plan.artifactRetentionDays === null ? 'без границ' : `${plan.artifactRetentionDays} дн` }}
              </dd>
            </div>
          </dl>

          <div class="lp-plan__feats">
            <span v-if="plan.heavyModel">сильная модель</span>
            <span v-if="plan.privateTemplates">свои шаблоны</span>
            <span v-if="plan.priority">приоритет в очереди</span>
            <span v-if="plan.isDefault">
              {{ welcomeDocs }}
              {{ pluralize(welcomeDocs, ['документ', 'документа', 'документов']) }} в подарок
            </span>
          </div>
        </article>
      </div>

      <div class="lp-pricing-foot">
        <a href="/register" class="lp-btn lp-btn--primary lp-btn--lg">Начать на пробном</a>
        <span class="lp-pricing-foot__note">
          Годовая предоплата — минус {{ pricing?.annualDiscountPct ?? 20 }} %. Отдельные границы
          докупаются расширениями, план менять не обязательно. Не оплатили вовремя — работа
          продолжается ещё {{ pricing?.graceDays ?? 7 }}
          {{ pluralize(pricing?.graceDays ?? 7, ['день', 'дня', 'дней']) }}, и ничего не пропадает.
        </span>
      </div>
    </section>

    <!-- ─── 10 · частые вопросы ───────────────────────────────────────── -->
    <!--
      Вопросы и ответы приходят из src/site/faq.ts — тот же текст уезжает в
      разметку FAQPage. Разметка, разошедшаяся с видимым текстом, не просто
      бесполезна: за неё снимают расширенные результаты целиком, поэтому
      источник обязан быть один.
    -->
    <section id="faq" class="lp-wrap lp-section">
      <div class="lp-head">
        <span class="lp-num">10</span>
        <h2 class="lp-h2">Частые вопросы</h2>
      </div>
      <div class="lp-faq">
        <div v-for="item in faq" :key="item.q" class="lp-faq__item">
          <h3 class="lp-faq__q">{{ item.q }}</h3>
          <p class="lp-faq__a">{{ item.a }}</p>
        </div>
      </div>
    </section>

    <!-- ─── финальный призыв ──────────────────────────────────────────── -->
    <section class="lp-dark lp-cta">
      <div class="lp-wrap lp-cta__inner">
        <div>
          <div class="lp-cta__title">Следующее КП пусть соберёт агент</div>
          <div class="lp-cta__note">
            ПРОБНЫЙ — БЕСПЛАТНО · {{ welcomeDocs }}
            {{ pluralize(welcomeDocs, ['ДОКУМЕНТ', 'ДОКУМЕНТА', 'ДОКУМЕНТОВ']) }} В ПОДАРОК ·
            БЕЗ КАРТЫ · ПЕРВАЯ ВЕРСИЯ ≈ 15 МИНУТ
          </div>
        </div>
        <a href="/register" class="lp-btn lp-btn--light lp-btn--lg">Начать бесплатно</a>
      </div>
    </section>

    <div class="lp-meta lp-meta--foot">
      <div class="lp-wrap lp-meta__center">
        ЭТА СТРАНИЦА — ШАБЛОН «ЛЕНДИНГ ПРОДУКТА» ИЗ КАТАЛОГА KEYPI · СОБРАНА АГЕНТОМ ЗА
        17 МИНУТ · ОДИН ДОКУМЕНТ
      </div>
    </div>

    <!--
      Реквизиты в подвале — те же, что в счёте и в оферте.
      Набирать их здесь руками значит однажды разойтись с документами: лицо,
      которое принимает деньги, обязано называться одинаково везде.
    -->
    <footer class="lp-wrap lp-footer">
      <span class="lp-brand__mark lp-brand__mark--sm">K</span>
      <span class="lp-footer__legal">
        {{ legal?.company.name || legal?.company.fullName || 'Keypi' }}
        <template v-if="legal?.company.inn"> · ИНН {{ legal.company.inn }}</template>
      </span>
      <div class="lp-spacer" />
      <RouterLink to="/offer" class="lp-footer__link">Публичная оферта</RouterLink>
      <RouterLink to="/payment-rules" class="lp-footer__link">Приём платежей</RouterLink>
      <RouterLink to="/privacy" class="lp-footer__link">Персональные данные</RouterLink>
      <a v-if="legal?.company.email" :href="`mailto:${legal.company.email}`" class="lp-footer__link">
        {{ legal.company.email }}
      </a>
    </footer>
  </div>
</template>
